import { Dialog, DialogContent, DialogHeader } from "components/ui/dialog";
import { VatCompany } from "lib/models";
import { Input } from "components/ui/input";
import { Button } from "components/ui/button";
import { useEffect, useState } from "react";
import { apiPost } from "components/Common/Axios";
import { Icons } from "components/ui/icons";
import toast from "react-hot-toast";
import { popupZIndex } from "lib/utils";

const VatCompanyEditPopup = ({
  open,
  onOpenChange,
  isAdd,
  selectedVatCompany,
  vatCompanyList,
  setVatCompanyList,
  popupLevel,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  isAdd: boolean;
  selectedVatCompany: VatCompany | undefined;
  vatCompanyList: VatCompany[];
  setVatCompanyList: (vatCompanyList: VatCompany[]) => void;
  popupLevel: number;
}) => {
  const [vatCompany, setVatCompany] = useState<VatCompany | undefined>(
    selectedVatCompany
  );
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (isAdd) {
        setVatCompany({
          nickName: "",
          name: "",
          businessNo: "",
          ceoName: "",
          address: "",
          email: "",
          bussinessType: "",
          bussinessItem: "",
          depositName: "",
        } as VatCompany);
      } else {
        setVatCompany(selectedVatCompany);
      }
    }
  }, [open, isAdd, selectedVatCompany]);

  const handleSave = async () => {
    if (vatCompany?.nickName === "") {
      alert("이름을 입력하세요");
      return;
    }
    if (vatCompany?.name === "") {
      alert("단체명을 입력하세요");
      return;
    }
    if (vatCompany?.businessNo === "") {
      alert("사업자번호를 입력하세요");
      return;
    }
    if (vatCompany?.ceoName === "") {
      alert("대표자를 입력하세요");
      return;
    }
    if (vatCompany?.address === "") {
      alert("소재지를 입력하세요");
      return;
    }
    if (vatCompany?.email === "") {
      alert("이메일을 입력하세요");
      return;
    }

    setIsSaving(true);
    try {
      const response = await apiPost("/vat-company", {
        vatCompany,
        isAdd,
      });

      if (response.error) {
        toast.error("저장하지 못했습니다");
        console.error("handleSave error", response.error);
        return;
      }

      const newVatCompany = response.data as VatCompany;
      if (isAdd) {
        setVatCompanyList([...vatCompanyList, newVatCompany]);
      } else {
        setVatCompanyList(
          vatCompanyList.map((v) =>
            v.id === vatCompany?.id ? response.data : v
          )
        );
      }
    } catch (error) {
      toast.error("저장하지 못했습니다");
      console.error("handleSave error", error);
      return;
    } finally {
      setIsSaving(false);
    }

    onOpenChange(false);
  };

  if (!vatCompany) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className={`w-[calc(100%-1rem)] max-w-[calc(100%-1rem)] md:max-w-screen-sm max-h-[95%] overflow-x-hidden overflow-y-auto thin-scrollbar`}
        zIndex={popupZIndex(popupLevel)}
      >
        <DialogHeader>
          <div className="text-center text-xl font-bold text-gray-900 py-2">
            거래처 {isAdd ? "추가" : "편집"}
          </div>
        </DialogHeader>
        <div className="flex flex-col gap-y-2">
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">이름</div>
            <Input
              value={vatCompany?.nickName}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, nickName: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">단체명</div>
            <Input
              value={vatCompany?.name}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, name: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">사업자번호</div>
            <Input
              value={vatCompany?.businessNo}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, businessNo: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">대표자</div>
            <Input
              value={vatCompany?.ceoName}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, ceoName: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">소재지</div>
            <Input
              value={vatCompany?.address}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, address: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">이메일</div>
            <Input
              value={vatCompany?.email}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, email: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">업태</div>
            <Input
              value={vatCompany?.bussinessType}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, bussinessType: e.target.value })
              }
            />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">업종</div>
            <Input
              value={vatCompany?.bussinessItem}
              onChange={(e) =>
                setVatCompany({ ...vatCompany, bussinessItem: e.target.value })
              }
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr] items-center gap-x-5 gap-y-1">
            <div className="text-left sm:text-right text-sm text-gray-600">입금자명</div>
            <Input
              value={vatCompany?.depositName}
              onChange={(e) =>
                setVatCompany({
                  ...vatCompany,
                  depositName: e.target.value,
                })
              }
            />
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 py-4">
            <Button variant="primary" size="lg" onClick={() => handleSave()}>
              {isSaving && (
                // eslint-disable-next-line react/jsx-pascal-case
                <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
              )}
              확인
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => onOpenChange(false)}
            >
              취소
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VatCompanyEditPopup;
